/**
 * Recovery codes for accounts that have no email.
 *
 * A player under 14 never gives us an email address, so there is no reset link
 * to send. The code is the only way back in — which makes two things matter
 * more than they usually would:
 *
 * 1. It has to be copyable by a ten-year-old off a screen and onto paper. A
 *    base32 string is not; four short words and two digits are. Every word is
 *    3-8 letters, unambiguous, and drawn from the game's own vocabulary. Four
 *    words from 96 plus two digits is ~33 bits; codes issued before 2026-10-30
 *    have three words (~26 bits) and still work. Guessing is held back by the
 *    database's per-name lockout (check_recovery_code), not by length alone.
 * 2. It is stored only as a bcrypt hash (see set_recovery_code in the
 *    migration). We cannot read it back, which is the point — nobody can steal
 *    it from us — but it also means a lost code is a lost account, and the
 *    terms say so plainly.
 *
 * Spec: docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §5.
 */

/**
 * Four words then two digits, e.g. HAMBI-PICKLE-ROCKET-COMET-42. Three-word
 * codes (issued before 2026-10-30) still match, so nobody's saved code stops
 * working.
 */
export const RECOVERY_CODE_PATTERN = /^[A-Z]+(-[A-Z]+){2,3}-\d{2}$/;

/**
 * 96 words. No near-homophones, nothing that reads as a person's name, nothing
 * that could be mistaken for another entry when handwritten.
 */
export const RECOVERY_WORDS: readonly string[] = [
  'HAMBI',
  'PICKLE',
  'ROCKET',
  'NUGGET',
  'PATTY',
  'CHEESE',
  'BACON',
  'TOMATO',
  'ONION',
  'MUSTARD',
  'KETCHUP',
  'RELISH',
  'SESAME',
  'LETTUCE',
  'GHERKIN',
  'SAUCE',
  'TOWER',
  'CASTLE',
  'SHIELD',
  'ARROW',
  'SPEAR',
  'HAMMER',
  'ANVIL',
  'TORCH',
  'COMET',
  'PLANET',
  'GALAXY',
  'ORBIT',
  'METEOR',
  'NEBULA',
  'STARS',
  'LUNAR',
  'FROSTY',
  'BLIZZARD',
  'GLACIER',
  'ICICLE',
  'SNOWY',
  'WINTER',
  'FROZEN',
  'CHILLY',
  'VOLCANO',
  'EMBER',
  'CINDER',
  'MAGMA',
  'FLAME',
  'SCORCH',
  'BLAZE',
  'SPARK',
  'JUNGLE',
  'DESERT',
  'CANYON',
  'MEADOW',
  'FOREST',
  'HARBOR',
  'ISLAND',
  'VALLEY',
  'TURBO',
  'MEGA',
  'ULTRA',
  'SUPER',
  'HYPER',
  'JUMBO',
  'GIANT',
  'MIGHTY',
  'SPICY',
  'CRISPY',
  'TOASTY',
  'CRUNCHY',
  'SIZZLE',
  'SMOKY',
  'TANGY',
  'SAVORY',
  'WIZARD',
  'KNIGHT',
  'BARON',
  'JESTER',
  'HERALD',
  'SCOUT',
  'RANGER',
  'GUARD',
  'COPPER',
  'SILVER',
  'GOLDEN',
  'BRONZE',
  'CRYSTAL',
  'MARBLE',
  'VELVET',
  'AMBER',
  'PUZZLE',
  'RIDDLE',
  'SECRET',
  'HIDDEN',
  'MARVEL',
  'WONDER',
  'BRAVO',
  'SUMMIT',
];

/** Cryptographic by default; the injected generator exists so tests can pin it. */
function secureRandom(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] / 2 ** 32;
}

export function generateRecoveryCode(rand: () => number = secureRandom): string {
  const pick = (): string => RECOVERY_WORDS[Math.floor(rand() * RECOVERY_WORDS.length)];
  const digits = String(Math.floor(rand() * 100)).padStart(2, '0');
  return `${pick()}-${pick()}-${pick()}-${pick()}-${digits}`;
}

export function isWellFormedRecoveryCode(code: string): boolean {
  return RECOVERY_CODE_PATTERN.test(code.trim().toUpperCase());
}
